import React, { Component } from 'react'
import { navigate } from '@reach/router'

import { Table, Header, ListFilters, Page } from 'components'
import { PackApi } from 'api'
import { pages, Pack, operations } from 'types'
import { packEdit, addpack } from 'utils/endpoints'
import { isSuperAdmin } from 'utils/permission'
import { matchesListSearch } from 'utils/listFilters'
import { getListFilters, persistListFilters } from 'utils/listViewState'

interface Props {
  path: string
}

interface State {
  packs: Pack[]
  search: string
  ssmFilter: string
  visibilityFilter: string
}

export default class PacksPage extends Component<Props, State> {
  state: State = {
    packs: [],
    ...getListFilters(pages.PACK_LIST, {
      search: '',
      ssmFilter: 'all',
      visibilityFilter: 'all',
    }),
  }
  page?: Page

  async componentDidMount() {
    this.refreshPacks()
  }

  componentDidUpdate(_previousProps: Props, previousState: State) {
    const { search, ssmFilter, visibilityFilter } = this.state
    if (
      previousState.search !== search ||
      previousState.ssmFilter !== ssmFilter ||
      previousState.visibilityFilter !== visibilityFilter
    ) {
      persistListFilters(pages.PACK_LIST, { search, ssmFilter, visibilityFilter })
    }
  }

  refreshPacks = async () => {
    try {
      const packs = await PackApi.getPacks()
      this.setState({ packs })
    } catch (e) {}
  }

  onOperationClick = async (operation: operations, packId: string) => {
    if (operation === 'Modifica') {
      navigate(packEdit + packId)
    }
    if (operation === 'Cancella') {
      this.page?.showConfirmDialog('Cancellazione Pacchetto', 'Vuoi davvero cancellare questo pacchetto', async () => {
        try {
          await PackApi.deletePack(packId)
          this.page?.showNotification({ message: 'Pacchetto cancellato.', type: 'success' })
          this.refreshPacks()
        } catch (error) {}
      })
    }
    if (operation === 'Sincronizza') {
      this.page?.showConfirmDialog(
        'Sincronizzazione Pacchetto',
        'Vuoi davvero Sincronizzare questo pacchetto',
        async () => {
          try {
            await PackApi.syncPack(packId)
            this.page?.showNotification({ message: 'Pacchetto sincronizzato correttamente', type: 'info' })
          } catch (error) {}
        }
      )
    }
  }

  onEdit = (packId: string) => {
    navigate(packEdit + packId)
  }

  getValues = (packs: Pack[]): any => {
    const rows = packs.map((pack: Pack) => {
      const price = pack.price === 0 ? 'Gratuito' : pack.price + '€'
      const isSSM = pack.isSSM ? 'Si' : 'No'
      const isVisible = pack.isVisible ? 'Si' : 'No'
      const operations = []
      if (isSuperAdmin()) operations.push('Sincronizza')
      if (isSuperAdmin()) operations.push('Modifica')
      if (isSuperAdmin() && !pack.count) operations.push('Cancella')
      return {
        id: pack._id!,
        values: [pack.name, pack.count || 0, pack.order, price, pack.description, isSSM, isVisible],
        operations: operations,
      }
    })
    return rows
  }

  getFilteredPacks = () => {
    const { packs, search, ssmFilter, visibilityFilter } = this.state

    return packs.filter((pack) => {
      const matchesSearch = matchesListSearch(search, [pack.name, pack.description, pack.price, pack.order])
      const matchesSsm = ssmFilter === 'all' || String(pack.isSSM) === ssmFilter
      const matchesVisibility = visibilityFilter === 'all' || String(pack.isVisible) === visibilityFilter
      return matchesSearch && matchesSsm && matchesVisibility
    })
  }

  render() {
    const keys = ['Nome', 'N° domande', 'Ordine', 'Prezzo', 'Descrizione', 'SSM', 'Visibile']
    const filteredPacks = this.getFilteredPacks()
    const values = this.getValues(filteredPacks)
    const buttons = [{ title: 'Crea un Pacchetto', href: addpack }]
    return (
      <Page ref={(ref) => (this.page = ref!)} page={pages.PACK_LIST}>
        {isSuperAdmin() && <Header title={'Pacchetti'} buttons={buttons} />}
        {!isSuperAdmin() && <Header title={'Pacchetti'} />}
        <ListFilters
          searchValue={this.state.search}
          searchPlaceholder="Nome o descrizione del pacchetto"
          onSearchChange={(search) => this.setState({ search })}
          filters={[
            {
              key: 'ssmFilter',
              label: 'Pacchetto SSM',
              value: this.state.ssmFilter,
              options: [
                { label: 'Tutti', value: 'all' },
                { label: 'Sì', value: 'true' },
                { label: 'No', value: 'false' },
              ],
            },
            {
              key: 'visibilityFilter',
              label: 'Visibilità',
              value: this.state.visibilityFilter,
              options: [
                { label: 'Tutti', value: 'all' },
                { label: 'Visibili', value: 'true' },
                { label: 'Non visibili', value: 'false' },
              ],
            },
          ]}
          onFilterChange={(key, value) => {
            if (key === 'ssmFilter') this.setState({ ssmFilter: value })
            if (key === 'visibilityFilter') this.setState({ visibilityFilter: value })
          }}
          onReset={() => this.setState({ search: '', ssmFilter: 'all', visibilityFilter: 'all' })}
          resultCount={filteredPacks.length}
          totalCount={this.state.packs.length}
        />
        <Table keys={keys} rows={values} onOperationClick={this.onOperationClick} />
      </Page>
    )
  }
}
